'use client';

import { UPSCALE_MODELS } from '@app/api/upscale/_lib/upscaleSchemas';
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    type JSX,
    type PropsWithChildren,
} from 'react';

import { AiContentType } from '@videoblocks/events-ts/lib/storyblocks/content/ai/AiContentTypeEnum';

import { useVideoEditDeepLink } from '../../video-edit/_utils/useVideoEditDeepLink';
import { type VideoEditDeepLink } from '../../video-edit/_utils/videoEditDeepLink';
import { fetchCredits } from '../_actions/fetchCredits';
import { VIDEO_EDIT_FORM_DEFAULTS } from './VideoEditFormState';
import { VIDEO_GENERATION_FORM_DEFAULTS } from './VideoGenerationFormState';
import { type RecentGenerationResult } from './history/pickRecentGeneration';
import { useImageSearch } from './imageSearch/useImageSearch';
import { useImageGenerationPolling } from './polling/useImageGenerationPolling';
import { useVideoEditPolling } from './polling/useVideoEditPolling';
import { useVideoGenerationPolling } from './polling/useVideoGenerationPolling';
import { CONFIRMATION_MODAL_DEFAULTS, useConfirmationModal } from './reducers/useConfirmationModal';
import { IMAGE_GENERATION_SUBMIT_DEFAULTS, useImageGenerationSubmit } from './reducers/useImageGenerationSubmit';
import { VIDEO_EDIT_SUBMIT_DEFAULTS, useVideoEditSubmit } from './reducers/useVideoEditSubmit';
import { VIDEO_GENERATION_SUBMIT_DEFAULTS, useVideoGenerationSubmit } from './reducers/useVideoGenerationSubmit';
import {
    VOICEOVER_GENERATION_SUBMIT_DEFAULTS,
    useVoiceoverGenerationSubmit,
} from './reducers/useVoiceoverGenerationSubmit';
import {
    AI_TOOLKIT_CATEGORIES,
    AI_TOOLKIT_OPERATIONS,
    AI_USER_SEGMENTS,
    SUPPORTED_VIDEO_SEARCH_MEDIA_TYPES,
    type ActiveTask,
    type AiToolkitActiveStatus,
    type AiToolkitOperation,
    type AiToolkitSubmitStatus,
    type AiUserSegment,
    type AiToolkitCategory,
    type AiToolkitContextValue,
    type AiToolkitFormStates,
    type AiToolkitSubmitStates,
    type ImageGenerationSubmitState,
    type ImageSearchReturn,
    type LastCompletedVideoEdit,
    type VideoEditSubmitState,
    type VideoEditSubmitStatus,
    type VideoGenerationSubmitState,
    type VideoSearchReturn,
} from './types';
import { useVideoSearch } from './videoSearch/useVideoSearch';

type AcknowledgedTaskStatuses = Partial<Record<AiToolkitCategory, AiToolkitActiveStatus>>;

const noop = (): void => {};
const asyncNoop = async (): Promise<void> => {};

const DEFAULT_IMAGE_SEARCH: ImageSearchReturn = {
    searchTerm: '',
    setSearchTerm: noop,
    mediaType: 'Images',
    setMediaType: noop,
    results: [],
    isLoading: true,
    error: null,
    hasSearched: false,
    totalCount: 0,
    hasMore: false,
    loadMore: asyncNoop,
    search: asyncNoop,
    reset: noop,
    currentPage: 1,
    totalPages: 1,
    goToPage: asyncNoop,
    showSearch: false,
    setShowSearch: noop,
};

const DEFAULT_VIDEO_SEARCH: VideoSearchReturn = {
    searchTerm: '',
    setSearchTerm: noop,
    mediaType: SUPPORTED_VIDEO_SEARCH_MEDIA_TYPES[0],
    results: [],
    isLoading: true,
    error: null,
    hasSearched: false,
    totalCount: 0,
    hasMore: false,
    loadMore: asyncNoop,
    search: asyncNoop,
    reset: noop,
    currentPage: 1,
    totalPages: 1,
    goToPage: asyncNoop,
    showSearch: false,
    setShowSearch: noop,
};

export const DEFAULT_FORM_STATES: AiToolkitFormStates = {
    [AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION]: VIDEO_GENERATION_FORM_DEFAULTS,
};

export const DEFAULT_SUBMIT_STATES: AiToolkitSubmitStates = {
    [AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION]: VIDEO_GENERATION_SUBMIT_DEFAULTS,
    [AI_TOOLKIT_CATEGORIES.VIDEO_EDIT]: VIDEO_EDIT_SUBMIT_DEFAULTS,
    [AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION]: IMAGE_GENERATION_SUBMIT_DEFAULTS,
    [AI_TOOLKIT_CATEGORIES.VOICEOVER]: VOICEOVER_GENERATION_SUBMIT_DEFAULTS,
};

export const DEFAULT_CATEGORY_SEGMENTS: Record<AiToolkitCategory, AiUserSegment> = {
    [AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION]: AI_USER_SEGMENTS.UNSUBSCRIBED,
    [AI_TOOLKIT_CATEGORIES.VIDEO_EDIT]: AI_USER_SEGMENTS.UNSUBSCRIBED,
    [AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION]: AI_USER_SEGMENTS.UNSUBSCRIBED,
    [AI_TOOLKIT_CATEGORIES.VOICEOVER]: AI_USER_SEGMENTS.UNSUBSCRIBED,
};

export const AiToolkitContext = createContext<AiToolkitContextValue>({
    credits: 0,
    isEnterprise: false,
    subscriptionId: null,
    categorySegments: DEFAULT_CATEGORY_SEGMENTS,
    activeTask: null,
    acknowledgeTask: noop,
    refreshCredits: () => Promise.resolve(0),
    formStates: DEFAULT_FORM_STATES,
    submitStates: DEFAULT_SUBMIT_STATES,
    updateFormState: noop,
    dispatchVideoGenerationSubmit: noop,
    dispatchVideoEditSubmit: noop,
    dispatchImageGenerationSubmit: noop,
    dispatchVoiceoverGenerationSubmit: noop,
    confirmationModal: CONFIRMATION_MODAL_DEFAULTS,
    dispatchConfirmationModal: noop,
    imageSearch: DEFAULT_IMAGE_SEARCH,
    videoSearch: DEFAULT_VIDEO_SEARCH,
    videoGenerationFileErrorMessage: null,
    setVideoGenerationFileErrorMessage: noop,
    imageGenerationFileErrorMessage: null,
    setImageGenerationFileErrorMessage: noop,
    videoGenerationSubmitError: null,
    setVideoGenerationSubmitError: noop,
    imageGenerationSubmitError: null,
    setImageGenerationSubmitError: noop,
    videoEditSubmitError: null,
    setVideoEditSubmitError: noop,
});

export type AiToolkitProviderProps = PropsWithChildren<{
    initialCredits: number;
    isEnterprise: boolean;
    subscriptionId: string | null;
    categorySegments: Record<AiToolkitCategory, AiUserSegment>;
    initialVideoGenerationResult?: RecentGenerationResult | null;
    initialVideoAiContentIds?: { aiContentId: string; lastAiContentId: string };
    initialImageGenerationResult?: RecentGenerationResult | null;
    initialImageAiContentIds?: { aiContentId: string; lastAiContentId: string };
    initialVideoEditResult?: RecentGenerationResult | null;
    initialVideoEditAiContentIds?: { aiContentId: string; lastAiContentId: string };
    initialUpscaleVideoEditAiContentIds?: { aiContentId: string; lastAiContentId: string };
}>;

function mapTaskTypeToAiContentType(taskType: string | null | undefined): AiContentType | null {
    switch (taskType) {
        case 'image_to_video':
            return AiContentType.IMAGE_TO_VIDEO;
        case 'text_to_video':
            return AiContentType.TEXT_TO_VIDEO;
        case 'text_to_image':
            return AiContentType.TEXT_TO_IMAGE;
        case 'image_to_image':
            return AiContentType.IMAGE_TO_IMAGE;
        case 'content_edit':
            return AiContentType.AI_VIDEO_EDIT;
        case 'upscale_video_edit':
            return AiContentType.UPSCALE_VIDEO_EDIT;
        default:
            return null;
    }
}

function mapTaskTypeToOperation(taskType: string | null | undefined): AiToolkitOperation {
    return taskType === 'upscale_video_edit' ? AI_TOOLKIT_OPERATIONS.UPSCALE : AI_TOOLKIT_OPERATIONS.GENERATE;
}

/** History does not record which model an edit ran on, but an upscale only ever runs on one. */
function restoredVideoEditAiModelName(operation: AiToolkitOperation): string | null {
    return operation === AI_TOOLKIT_OPERATIONS.UPSCALE ? UPSCALE_MODELS[0] : null;
}

function computeInitialVideoState(
    result: RecentGenerationResult | null | undefined,
    eventIds: { aiContentId: string; lastAiContentId: string } | undefined,
): VideoGenerationSubmitState {
    const base: VideoGenerationSubmitState = {
        ...VIDEO_GENERATION_SUBMIT_DEFAULTS,
        aiContentId: eventIds?.aiContentId ?? '',
        lastAiContentId: eventIds?.lastAiContentId ?? '',
    };
    if (!result) return base;
    if (result.state === 'inProgress') {
        return {
            ...base,
            status: 'inProgress',
            pendingTaskId: result.taskId,
            sourceThumbnailUrl: result.sourceThumbnailUrl ?? '',
            contentId: result.contentId ?? null,
        };
    }
    return {
        ...base,
        status: 'success',
        videoUrl: result.downloadUrl,
        contentId: result.contentId ?? null,
        aiContentType: mapTaskTypeToAiContentType(result.taskType),
        restoredFromHistory: true,
    };
}

function computeInitialImageState(
    result: RecentGenerationResult | null | undefined,
    eventIds: { aiContentId: string; lastAiContentId: string } | undefined,
): ImageGenerationSubmitState {
    const base: ImageGenerationSubmitState = {
        ...IMAGE_GENERATION_SUBMIT_DEFAULTS,
        aiContentId: eventIds?.aiContentId ?? '',
        lastAiContentId: eventIds?.lastAiContentId ?? '',
    };
    if (!result) return base;
    if (result.state === 'inProgress') {
        return { ...base, status: 'inProgress', pendingTaskId: result.taskId };
    }
    return {
        ...base,
        status: 'success',
        imageUrl: result.downloadUrl,
        aiContentType: mapTaskTypeToAiContentType(result.taskType),
        restoredFromHistory: true,
    };
}

function computeInitialVideoEditState(
    result: RecentGenerationResult | null | undefined,
    eventIdsByOperation: Record<AiToolkitOperation, { aiContentId: string; lastAiContentId: string } | undefined>,
    deepLink: VideoEditDeepLink | null,
): VideoEditSubmitState {
    const operation = mapTaskTypeToOperation(result?.taskType);
    // Upscales chain their IDs in their own cookies, apart from the edits they upscale.
    const eventIds = eventIdsByOperation[operation];
    const base: VideoEditSubmitState = {
        ...VIDEO_EDIT_SUBMIT_DEFAULTS,
        aiContentId: eventIds?.aiContentId ?? '',
        lastAiContentId: eventIds?.lastAiContentId ?? '',
    };
    // A running generation takes precedence over deepLinks to prevent users from selecting a new clip while a generation is in progress.
    if (result?.state === 'inProgress') {
        return {
            ...base,
            status: 'inProgress',
            operation,
            pendingTaskId: result.taskId,
            aiContentType: mapTaskTypeToAiContentType(result.taskType),
            aiModelName: restoredVideoEditAiModelName(operation),
            originalVideoUrl: result.sourcePreviewUrl ?? '',
            originalThumbnailUrl: result.sourceThumbnailUrl ?? '',
            // An upscale restores the edit it runs on, which backs the in-progress view and gives a
            // retry its `parentTaskId`.
            editedVideoUrl: result.parent?.downloadUrl ?? '',
            completedTaskId: result.parent?.taskId ?? null,
            startTime: result.startTime,
            duration: result.duration!,
            aspectRatio: result.aspectRatio,
            contentId: result.contentId ?? null,
        };
    }
    const completedSnapshot = buildCompletedSnapshot(result, eventIds);

    // Deep link from Stockblocks takes precedence over a finished generation restored from history.
    if (deepLink) {
        return { ...base, status: 'selection', lastCompletedEdit: completedSnapshot };
    }
    if (!completedSnapshot) return base;

    return {
        ...base,
        ...completedSnapshot,
        status: 'success',
        startTime: result!.startTime,
        aspectRatio: result!.aspectRatio,
        duration: result!.duration!,
        contentId: result!.contentId ?? null,
        restoredFromHistory: true,
        lastCompletedEdit: completedSnapshot,
    };
}

function computeInitialFormStates(
    result: RecentGenerationResult | null | undefined,
    deepLink: VideoEditDeepLink | null,
): AiToolkitFormStates {
    // Ensures we don't include the pre-existing form inputs from a previous generation while showing a deep link.
    // If we've restored to inProgress, we want to maintain the inputs from the restored result so that it can forward those properties to success/error.
    if (deepLink && result?.state !== 'inProgress') return {};

    return {
        [AI_TOOLKIT_CATEGORIES.VIDEO_EDIT]: {
            ...VIDEO_EDIT_FORM_DEFAULTS,
            startTime: result?.startTime ?? 0,
            duration: result?.duration ?? VIDEO_EDIT_FORM_DEFAULTS.duration,
            prompt: result?.promptText ?? '',
            contentId: result?.contentId ?? null,
            sourceVideo: result?.sourcePreviewUrl ?? '',
        },
    };
}

function buildCompletedSnapshot(
    result: RecentGenerationResult | null | undefined,
    eventIds: { aiContentId: string; lastAiContentId: string } | undefined,
): LastCompletedVideoEdit | null {
    if (result?.state !== 'completed') return null;

    const aiContentType = mapTaskTypeToAiContentType(result.taskType);
    const operation = mapTaskTypeToOperation(result.taskType);
    return {
        editedVideoUrl: result.downloadUrl,
        completedTaskId: result.taskId,
        operation,
        originalVideoUrl: result.sourcePreviewUrl ?? '',
        originalThumbnailUrl: result.sourceThumbnailUrl ?? '',
        aiContentId: eventIds?.aiContentId ?? '',
        lastAiContentId: eventIds?.lastAiContentId ?? '',
        aiContentType,
        aiModelName: restoredVideoEditAiModelName(operation),
        startTime: result.startTime,
        duration: result.duration!,
        contentId: result.contentId ?? null,
        aspectRatio: result.aspectRatio,
    };
}

function deriveActiveTask(
    videoState: AiToolkitSubmitStates[typeof AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION],
    videoEditState: AiToolkitSubmitStates[typeof AI_TOOLKIT_CATEGORIES.VIDEO_EDIT],
    imageState: AiToolkitSubmitStates[typeof AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION],
    voiceoverStatus: AiToolkitSubmitStatus,
    acknowledgedTaskStatuses: AcknowledgedTaskStatuses,
): ActiveTask {
    // `VideoEditSubmitStatus` is a superset of `AiToolkitSubmitStatus`; its extra `selection` status
    // matches none of the lookups below, so a pending selection never counts as an active task.
    // Video edit is the only category with a second operation; the rest are always generates.
    const entries: Array<{
        category: AiToolkitCategory;
        operation: AiToolkitOperation;
        status: VideoEditSubmitStatus;
        exclude?: boolean;
    }> = [
        {
            category: AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION,
            operation: AI_TOOLKIT_OPERATIONS.GENERATE,
            status: videoState.status,
            exclude:
                videoState.restoredFromHistory ||
                acknowledgedTaskStatuses[AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION] === videoState.status,
        },
        {
            category: AI_TOOLKIT_CATEGORIES.VIDEO_EDIT,
            operation: videoEditState.operation,
            status: videoEditState.status,
            exclude:
                videoEditState.restoredFromHistory ||
                acknowledgedTaskStatuses[AI_TOOLKIT_CATEGORIES.VIDEO_EDIT] === videoEditState.status,
        },
        {
            category: AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION,
            operation: AI_TOOLKIT_OPERATIONS.GENERATE,
            status: imageState.status,
            exclude:
                imageState.restoredFromHistory ||
                acknowledgedTaskStatuses[AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION] === imageState.status,
        },
        {
            category: AI_TOOLKIT_CATEGORIES.VOICEOVER,
            operation: AI_TOOLKIT_OPERATIONS.GENERATE,
            status: voiceoverStatus,
            exclude: acknowledgedTaskStatuses[AI_TOOLKIT_CATEGORIES.VOICEOVER] === voiceoverStatus,
        },
    ];
    const inProgress = entries.find((e) => e.status === 'inProgress' && !e.exclude);
    if (inProgress) {
        return { category: inProgress.category, operation: inProgress.operation, status: 'inProgress' };
    }
    const success = entries.find((e) => e.status === 'success' && !e.exclude);
    if (success) return { category: success.category, operation: success.operation, status: 'success' };
    const error = entries.find((e) => e.status === 'error' && !e.exclude);
    if (error) return { category: error.category, operation: error.operation, status: 'error' };
    return null;
}

export const AiToolkitProvider = ({
    children,
    initialCredits,
    isEnterprise,
    subscriptionId,
    categorySegments,
    initialVideoGenerationResult,
    initialVideoAiContentIds,
    initialImageGenerationResult,
    initialImageAiContentIds,
    initialVideoEditResult,
    initialVideoEditAiContentIds,
    initialUpscaleVideoEditAiContentIds,
}: AiToolkitProviderProps): JSX.Element => {
    const videoEditDeepLink = useVideoEditDeepLink();
    const [credits, setCredits] = useState(initialCredits);
    const [formStates, setFormStates] = useState<AiToolkitFormStates>(() =>
        computeInitialFormStates(initialVideoEditResult, videoEditDeepLink),
    );
    const [videoGenerationFileErrorMessage, setVideoGenerationFileErrorMessage] = useState<string | null>(null);
    const [imageGenerationFileErrorMessage, setImageGenerationFileErrorMessage] = useState<string | null>(null);
    const [videoGenerationSubmitError, setVideoGenerationSubmitError] = useState<string | null>(null);
    const [imageGenerationSubmitError, setImageGenerationSubmitError] = useState<string | null>(null);
    const [videoEditSubmitError, setVideoEditSubmitError] = useState<string | null>(null);
    const [videoGenerationSubmitState, dispatchVideoGenerationSubmit] = useVideoGenerationSubmit(
        computeInitialVideoState(initialVideoGenerationResult, initialVideoAiContentIds),
    );
    const [imageGenerationSubmitState, dispatchImageGenerationSubmit] = useImageGenerationSubmit(
        computeInitialImageState(initialImageGenerationResult, initialImageAiContentIds),
    );
    const [videoEditSubmitState, dispatchVideoEditSubmit] = useVideoEditSubmit(
        computeInitialVideoEditState(
            initialVideoEditResult,
            {
                [AI_TOOLKIT_OPERATIONS.GENERATE]: initialVideoEditAiContentIds,
                [AI_TOOLKIT_OPERATIONS.UPSCALE]: initialUpscaleVideoEditAiContentIds,
            },
            videoEditDeepLink,
        ),
    );
    const [voiceoverGenerationSubmitState, dispatchVoiceoverGenerationSubmit] = useVoiceoverGenerationSubmit();
    const [confirmationModal, dispatchConfirmationModal] = useConfirmationModal();
    const imageSearch = useImageSearch();
    const videoSearch = useVideoSearch();
    const [acknowledgedTaskStatuses, setAcknowledgedTaskStatuses] = useState<AcknowledgedTaskStatuses>({});

    const acknowledgeTask = useCallback((category: AiToolkitCategory, status: AiToolkitActiveStatus): void => {
        setAcknowledgedTaskStatuses((prev) => ({ ...prev, [category]: status }));
    }, []);

    // Once a category starts a new attempt, any acknowledgment of its previous outcome no longer
    // applies to the outcome this attempt will produce.
    useEffect(() => {
        setAcknowledgedTaskStatuses((prev) => {
            const next = { ...prev };
            let changed = false;
            for (const [category, status] of [
                [AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION, videoGenerationSubmitState.status],
                [AI_TOOLKIT_CATEGORIES.VIDEO_EDIT, videoEditSubmitState.status],
                [AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION, imageGenerationSubmitState.status],
                [AI_TOOLKIT_CATEGORIES.VOICEOVER, voiceoverGenerationSubmitState.status],
            ] as const) {
                if (status === 'inProgress' && category in next) {
                    delete next[category];
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, [
        videoGenerationSubmitState.status,
        videoEditSubmitState.status,
        imageGenerationSubmitState.status,
        voiceoverGenerationSubmitState.status,
    ]);

    const activeTask = useMemo(
        () =>
            deriveActiveTask(
                videoGenerationSubmitState,
                videoEditSubmitState,
                imageGenerationSubmitState,
                voiceoverGenerationSubmitState.status,
                acknowledgedTaskStatuses,
            ),
        [
            videoGenerationSubmitState,
            videoEditSubmitState,
            imageGenerationSubmitState,
            voiceoverGenerationSubmitState.status,
            acknowledgedTaskStatuses,
        ],
    );

    const submitStates: AiToolkitSubmitStates = useMemo(
        () => ({
            [AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION]: videoGenerationSubmitState,
            [AI_TOOLKIT_CATEGORIES.VIDEO_EDIT]: videoEditSubmitState,
            [AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION]: imageGenerationSubmitState,
            [AI_TOOLKIT_CATEGORIES.VOICEOVER]: voiceoverGenerationSubmitState,
        }),
        [videoGenerationSubmitState, videoEditSubmitState, imageGenerationSubmitState, voiceoverGenerationSubmitState],
    );

    const refreshCredits = useCallback(async (): Promise<number> => {
        const balance = await fetchCredits();
        setCredits(balance);
        return balance;
    }, []);

    const updateFormState = useCallback(
        <K extends keyof AiToolkitFormStates>(category: K, state: AiToolkitFormStates[K]): void => {
            setFormStates((prev) => ({ ...prev, [category]: state }));
        },
        [],
    );

    useVideoGenerationPolling({ videoGenerationSubmitState, dispatchVideoGenerationSubmit, refreshCredits });
    useImageGenerationPolling({ imageGenerationSubmitState, dispatchImageGenerationSubmit, refreshCredits });
    useVideoEditPolling({ videoEditSubmitState, dispatchVideoEditSubmit, refreshCredits });

    return (
        <AiToolkitContext.Provider
            value={{
                credits,
                isEnterprise,
                subscriptionId,
                categorySegments,
                activeTask,
                acknowledgeTask,
                refreshCredits,
                formStates,
                submitStates,
                updateFormState,
                dispatchVideoGenerationSubmit,
                dispatchVideoEditSubmit,
                dispatchImageGenerationSubmit,
                dispatchVoiceoverGenerationSubmit,
                confirmationModal,
                dispatchConfirmationModal,
                imageSearch,
                videoSearch,
                videoGenerationFileErrorMessage,
                setVideoGenerationFileErrorMessage,
                imageGenerationFileErrorMessage,
                setImageGenerationFileErrorMessage,
                videoGenerationSubmitError,
                setVideoGenerationSubmitError,
                imageGenerationSubmitError,
                setImageGenerationSubmitError,
                videoEditSubmitError,
                setVideoEditSubmitError,
            }}
        >
            {children}
        </AiToolkitContext.Provider>
    );
};

export const useAiToolkitContext = (): AiToolkitContextValue => {
    const context = useContext(AiToolkitContext);

    return context;
};
